import React, { useMemo, useState } from "react";

const normalizeWhitespace = (value) => String(value || "").replace(/\s+/g, " ").trim();

const toTitleCase = (value) =>
  normalizeWhitespace(value)
    .split(" ")
    .filter(Boolean)
    .map((part) => `${part.charAt(0).toUpperCase()}${part.slice(1).toLowerCase()}`)
    .join(" ");

const CompanyBulkActions = ({
  show = false,
  selectedRows = [],
  isMerging = false,
  isDeleting = false,
  error = "",
  onMerge,
  onDelete,
}) => {
  const [showMergeConfirm, setShowMergeConfirm] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [nameMode, setNameMode] = useState("existing");
  const [existingName, setExistingName] = useState("");
  const [customName, setCustomName] = useState("");

  const nameOptions = useMemo(() => {
    const seen = new Set();
    const options = [];
    selectedRows.forEach((row) => {
      const name = normalizeWhitespace(row?.ai_name);
      if (!name) {
        return;
      }
      const key = name.toLowerCase();
      if (seen.has(key)) {
        return;
      }
      seen.add(key);
      options.push(name);
    });
    return options;
  }, [selectedRows]);

  const mergeDisabled = selectedRows.length < 2 || isMerging || isDeleting;
  const deleteDisabled = selectedRows.length < 1 || isMerging || isDeleting;

  if (!show) {
    return null;
  }

  const submitMerge = () => {
    const resolvedName =
      nameMode === "custom" ? toTitleCase(customName) : normalizeWhitespace(existingName);
    if (!resolvedName) {
      return;
    }
    onMerge?.({
      primaryNameMode: nameMode,
      primaryName: resolvedName,
    });
  };

  return (
    <div className="mt-4 flex flex-col gap-3 rounded-2xl border border-sky-500/20 bg-slate-950/50 p-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-slate-300">
          <span className="font-semibold text-slate-100">{selectedRows.length}</span> company row
          {selectedRows.length === 1 ? "" : "s"} selected
        </p>
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => setShowMergeConfirm(true)}
            disabled={mergeDisabled}
            className="rounded-lg border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-xs font-semibold text-amber-100 transition-colors hover:border-amber-400 hover:text-white disabled:cursor-not-allowed disabled:opacity-40"
          >
            Merge Companies
          </button>
          <button
            type="button"
            onClick={() => setShowDeleteConfirm(true)}
            disabled={deleteDisabled}
            className="rounded-lg border border-rose-500/40 bg-rose-500/10 px-3 py-2 text-xs font-semibold text-rose-100 transition-colors hover:border-rose-400 hover:text-white disabled:cursor-not-allowed disabled:opacity-40"
          >
            Delete Companies
          </button>
        </div>
      </div>

      {showMergeConfirm ? (
        <div className="rounded-xl border border-slate-700/80 bg-slate-950 p-4">
          <p className="text-sm font-semibold text-slate-100">Merge selected companies?</p>
          <p className="mt-1 text-xs text-slate-400">
            Choose the canonical company name. Other selected names become aliases.
          </p>
          <div className="mt-3 space-y-3">
            <label className="flex items-center gap-2 text-xs text-slate-300">
              <input
                type="radio"
                name="company-name-mode"
                checked={nameMode === "existing"}
                onChange={() => {
                  setNameMode("existing");
                  if (!existingName && nameOptions.length) {
                    setExistingName(nameOptions[0]);
                  }
                }}
              />
              Use selected company name
            </label>
            {nameMode === "existing" ? (
              <select
                value={existingName || nameOptions[0] || ""}
                onChange={(event) => setExistingName(event.target.value)}
                className="w-full rounded-lg border border-slate-700 bg-slate-900 px-3 py-2 text-sm text-slate-100"
              >
                {nameOptions.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
            ) : null}
            <label className="flex items-center gap-2 text-xs text-slate-300">
              <input
                type="radio"
                name="company-name-mode"
                checked={nameMode === "custom"}
                onChange={() => setNameMode("custom")}
              />
              Use custom canonical name
            </label>
            {nameMode === "custom" ? (
              <input
                type="text"
                value={customName}
                onChange={(event) => setCustomName(event.target.value)}
                placeholder="Enter company name"
                className="w-full rounded-lg border border-slate-700 bg-slate-900 px-3 py-2 text-sm text-slate-100"
              />
            ) : null}
          </div>
          <div className="mt-4 flex justify-end gap-2">
            <button
              type="button"
              onClick={() => setShowMergeConfirm(false)}
              disabled={isMerging}
              className="rounded-lg border border-slate-700 bg-slate-900 px-3 py-2 text-xs font-semibold text-slate-200"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={submitMerge}
              disabled={isMerging}
              className="rounded-lg border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-xs font-semibold text-amber-100"
            >
              {isMerging ? "Merging..." : "Confirm Merge"}
            </button>
          </div>
        </div>
      ) : null}

      {showDeleteConfirm ? (
        <div className="rounded-xl border border-slate-700/80 bg-slate-950 p-4">
          <p className="text-sm font-semibold text-slate-100">Delete selected companies?</p>
          <p className="mt-1 text-xs text-slate-400">
            This is a soft delete. Selected records are tombstoned and hidden from data view.
          </p>
          <div className="mt-4 flex justify-end gap-2">
            <button
              type="button"
              onClick={() => setShowDeleteConfirm(false)}
              disabled={isDeleting}
              className="rounded-lg border border-slate-700 bg-slate-900 px-3 py-2 text-xs font-semibold text-slate-200"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={() => onDelete?.()}
              disabled={isDeleting}
              className="rounded-lg border border-rose-500/40 bg-rose-500/10 px-3 py-2 text-xs font-semibold text-rose-100"
            >
              {isDeleting ? "Deleting..." : "Confirm Delete"}
            </button>
          </div>
        </div>
      ) : null}

      {error ? (
        <div className="rounded-xl border border-rose-500/20 bg-rose-500/10 px-4 py-3 text-sm text-rose-100">
          {error}
        </div>
      ) : null}
    </div>
  );
};

export default CompanyBulkActions;

